import React, { useRef, useState } from "react";
import { MailCheck, Upload, CheckCircle2, AlertTriangle, SkipForward, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const STYLE = { ok: "text-palm", skip: "text-gold", error: "text-crimson" };
const ICON = { ok: CheckCircle2, skip: SkipForward, error: AlertTriangle };

function Row({ r }) {
  const Icon = ICON[r.status];
  return (
    <tr className="border-b border-gold/15 text-xs" data-testid={`email-row-${r.line}`}>
      <td className="py-1.5 pr-2 font-mono-accent">{r.member_id || "—"}</td>
      <td className="py-1.5 pr-2 font-medium">{r.name || "—"}</td>
      <td className="py-1.5 pr-2 font-mono-accent">{r.email || "—"}</td>
      <td className={`py-1.5 ${STYLE[r.status]}`}><span className="flex items-center gap-1"><Icon size={12} /> {r.problems.join(", ") || "ready to activate"}</span></td>
    </tr>
  );
}

function Results({ results }) {
  const ok = results.filter((r) => r.status === "activated");
  return (
    <div className="space-y-3" data-testid="email-results">
      <div className="grid grid-cols-2 gap-2 text-center text-sm">
        <div className="rounded-lg bg-palm/10 p-2"><p className="text-2xl font-bold text-palm" data-testid="email-activated">{ok.length}</p>activated &amp; invited</div>
        <div className="rounded-lg bg-crimson/10 p-2"><p className="text-2xl font-bold text-crimson" data-testid="email-failed">{results.length - ok.length}</p>failed</div>
      </div>
      <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
        {results.map((r) => <li key={r.member_id + r.email} className="flex justify-between"><span>{r.name || r.member_id} · {r.email}</span><span className={r.status === "activated" ? "text-palm" : "text-crimson"}>{r.status}{r.invite ? ` · email ${r.invite}` : ""}</span></li>)}
      </ul>
    </div>
  );
}

export function EmailCollectionImport({ onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const reset = () => { setPreview(null); setResults(null); };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import/emails/preview", fd); setPreview(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    const rows = preview.rows.filter((r) => r.status === "ok").map(({ member_id, email }) => ({ member_id, email }));
    try { const { data } = await api.post("/admin/import/emails/run", { rows }); setResults(data.results); toast.success(`${data.results.filter((r) => r.status === "activated").length} member(s) activated`); onDone?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <Button variant="outline" className="gap-2 border-palm text-palm hover:bg-palm/10" onClick={() => setOpen(true)} data-testid="email-import-btn"><MailCheck size={15} /> Upload collected emails</Button>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>Activate members from collected emails</DialogTitle></DialogHeader>
        {results ? <Results results={results} /> : !preview ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Upload the <span className="font-mono-accent">members-missing-email.csv</span> you exported, with the <span className="font-mono-accent">Email (fill in)</span> column completed. Each matched record becomes a login account and receives its set-password invite by email (and SMS if a phone is on file).</p>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="email-import-file" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy} className="w-full bg-crimson" data-testid="email-import-choose">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />} Choose CSV</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 text-sm" data-testid="email-summary">
              <span className="text-palm font-semibold">{preview.summary.ok} ready</span><span className="text-gold font-semibold">{preview.summary.skip} blank</span><span className="text-crimson font-semibold">{preview.summary.error} problems</span>
            </div>
            <div className="max-h-72 overflow-auto rounded-xl border border-gold/20">
              <table className="w-full text-left"><thead className="bg-gold-light text-[10px] uppercase tracking-wider"><tr><th className="p-2">Member ID</th><th className="p-2">Name</th><th className="p-2">Email</th><th className="p-2">Status</th></tr></thead>
                <tbody>{preview.rows.map((r) => <Row key={r.line} r={r} />)}</tbody></table>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} data-testid="email-import-back">Choose another file</Button>
              <Button onClick={run} disabled={busy || preview.summary.ok === 0} className="flex-1 bg-palm hover:bg-palm/90" data-testid="email-import-run">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <MailCheck size={16} className="mr-2" />} Activate {preview.summary.ok} member(s) &amp; send invites</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
